import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getManagedCategories, getManagedSubcategories, createSubcategory, deleteSubcategory } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Trash2, Tag, Loader2, Layers } from 'lucide-react';

import { useDepartments } from '../hooks/useDepartments';

const SubCategoryManagementPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'Super Admin';
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newSubName, setNewSubName] = useState('');
  const [selectedDept, setSelectedDept] = useState(isSuperAdmin ? 'Human Resources Management' : user?.department || '');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [adding, setAdding] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedDept) return;
    try {
      setLoading(true);
      const [catsRes, subsRes] = await Promise.all([
        getManagedCategories(selectedDept),
        getManagedSubcategories({ department: selectedDept }),
      ]);
      setCategories(catsRes.data);
      setSubcategories(subsRes.data);
    } catch {
      toast.error('Failed to load sub-categories');
    } finally {
      setLoading(false);
    }
  }, [selectedDept]);

  useEffect(() => { loadData(); }, [loadData]);

  // Reset category filter when dept changes
  useEffect(() => { setSelectedCategory(''); }, [selectedDept]);

  const grouped = useMemo(() => {
    const map = {};
    subcategories.forEach(s => {
      if (!map[s.category]) map[s.category] = [];
      map[s.category].push(s);
    });
    return map;
  }, [subcategories]);

  const visibleCategories = useMemo(() => {
    if (selectedCategory) return categories.filter(c => c.name === selectedCategory);
    return categories;
  }, [categories, selectedCategory]);

  const handleAdd = async () => {
    if (!newSubName.trim()) return toast.error('Enter a sub-category name');
    if (!selectedDept) return toast.error('Select a department');
    if (!selectedCategory) return toast.error('Select a parent category');
    try {
      setAdding(true);
      await createSubcategory({ name: newSubName.trim(), category: selectedCategory, department: selectedDept });
      toast.success('Sub-category added');
      setNewSubName('');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add sub-category');
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, name) => {
    try {
      await deleteSubcategory(id);
      toast.success(`"${name}" deleted`);
      loadData();
    } catch {
      toast.error('Failed to delete sub-category');
    }
  };

  if (user?.role !== 'Super Admin' && user?.role !== 'Admin') {
    return <div className="text-center py-12 text-muted-foreground">You don't have permission to manage sub-categories.</div>;
  }

  return (
    <div className="space-y-6" data-testid="subcategory-management-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Sub-Category Management</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Map sub-categories under each parent category. Sub-categories appear on task creation for their department.
        </p>
      </div>

      {/* Filters + Add form */}
      <Card className="border-border/50">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {isSuperAdmin && (
              <Select value={selectedDept} onValueChange={setSelectedDept}>
                <SelectTrigger className="w-64" data-testid="sub-dept-filter">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={selectedCategory || 'all'} onValueChange={(v) => setSelectedCategory(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-64" data-testid="sub-category-filter">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2 items-center pt-1">
            <Select value={selectedCategory || ''} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-64" data-testid="add-sub-parent-select">
                <SelectValue placeholder="Parent category for new sub..." />
              </SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input
              data-testid="new-subcategory-input"
              placeholder="New sub-category name..."
              value={newSubName}
              onChange={(e) => setNewSubName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              className="max-w-sm"
            />
            <Button data-testid="add-subcategory-btn" onClick={handleAdd} disabled={adding || !selectedCategory} size="sm">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />Add</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Grouped list */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : visibleCategories.length === 0 ? (
        <p className="text-muted-foreground text-sm py-8 text-center">No categories in {selectedDept}. Add categories first.</p>
      ) : (
        <div className="grid gap-4">
          {visibleCategories.map(cat => {
            const subs = grouped[cat.name] || [];
            return (
              <Card key={cat.id} className="border-border/50" data-testid={`sub-group-${cat.id}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Tag className="h-4 w-4 text-[#F5A623]" />
                    {cat.name}
                    <span className="text-xs font-normal text-muted-foreground">({subs.length})</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {subs.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No sub-categories yet</p>
                  ) : (
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {subs.map(s => (
                        <div key={s.id}
                          className="flex items-center justify-between gap-2 p-2 rounded-md border border-border/50 bg-muted/30 text-sm"
                          data-testid={`sub-item-${s.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <Layers className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="truncate">{s.name}</span>
                          </div>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10 shrink-0" data-testid={`delete-sub-${s.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Sub-Category</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{s.name}" under "{cat.name}"? This cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(s.id, s.name)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SubCategoryManagementPage;
